import { Box, Flex, LinkBox, Tag, Text } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useMemo } from 'react';

import { getReadingTime } from '#features/blog/utils/getReadingTime';
import { useFormatDistanceToNow } from '#features/date';
import { Image, Link } from '#features/ui';

type BlogHeroPostProps = {
  slug: string;
  previewImage: string;
  title: string;
  tags?: string;
  date?: string;
  ogDescription?: string;
  content?: string;
};

export const BlogHeroPost = ({
  title,
  previewImage,
  slug,
  tags,
  date,
  ogDescription,
  content,
}: BlogHeroPostProps) => {
  const { _ } = useLingui();
  const formatDistanceToNow = useFormatDistanceToNow();
  const readingTime = useMemo(() => (content ? getReadingTime(content) : null), [content]);

  return (
    <LinkBox
      display="flex"
      w="100%"
      overflow="hidden"
      borderRadius="2xl"
      flexDir={{ base: 'column', md: 'row' }}
      bg="brand.700"
      transition="all 200ms"
      _hover={{ bg: 'brand.600' }}
      css={{
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
      mb={8}
    >
      {/* Image */}
      <Box
        w={{ base: '100%', md: '60%' }}
        aspectRatio={{ base: '16 / 9', md: 'auto' }}
        minH={{ md: '320px' }}
        overflow="hidden"
        flexShrink={0}
      >
        <Image
          src={previewImage}
          alt={title}
          style={{
            objectFit: 'cover',
            objectPosition: 'top center',
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      {/* Content */}
      <Flex direction="column" p={{ base: 5, md: 8 }} gap={4} justify="center" flex={1}>
        {/* Tags + reading time */}
        <Flex gap={2} alignItems="center" flexWrap="wrap">
          {tags &&
            tags
              .split(',')
              .slice(0, 3)
              .map((tag) => (
                <Tag.Root key={tag} size="md">
                  <Tag.Label textStyle="label">{tag}</Tag.Label>
                </Tag.Root>
              ))}
          {readingTime && (
            <Text textStyle="xs" color="whiteAlpha.500">
              {readingTime} {_(msg({ id: 'blog.readingTime', message: 'min read' }))}
            </Text>
          )}
        </Flex>

        {/* Title */}
        <Text fontWeight="800" fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1.2" lineClamp={3}>
          {title}
        </Text>

        {/* Description */}
        {ogDescription && (
          <Text textStyle="md" lineHeight="1.6" color="whiteAlpha.600" lineClamp={3}>
            {ogDescription}
          </Text>
        )}

        {/* Date */}
        {date && (
          <Text textStyle="sm" color="whiteAlpha.400">
            {formatDistanceToNow(date)}
          </Text>
        )}
      </Flex>

      <Link to="/{-$locale}/blog/$slug" params={{ slug }} overlay />
    </LinkBox>
  );
};
